import { useState, useEffect, useContext } from "react";
import { Trophy, Users, Coins, Crown, Play, X, Plus, RefreshCw, ChevronRight, Swords, Medal, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../App";

// Game icons mapping
const GAME_ICONS = {
  poker: "🃏",
  chess: "♔",
  pool: "🎱",
  hangman: "🎯",
  blackjack: "🃏",
  roulette: "🎰"
};

const GAME_NAMES = {
  poker: "Texas Hold'em",
  chess: "Chess",
  pool: "8-Ball Pool",
  hangman: "Hangman",
  blackjack: "Blackjack",
  roulette: "Roulette"
};

// Tournament Card Component
const TournamentCard = ({ tournament, onJoin, onView, currentUser }) => {
  const isJoined = tournament.players?.some(p => p.username === currentUser);
  const isFull = tournament.players?.length >= tournament.max_players;
  const canJoin = tournament.status === "registration" && !isJoined && !isFull;
  
  const getStatusColor = (status) => {
    switch (status) {
      case "registration": return "bg-green-500";
      case "in_progress": return "bg-yellow-500";
      case "completed": return "bg-blue-500";
      case "cancelled": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className="retro-button p-3 hover:bg-purple-50 cursor-pointer" onClick={() => onView(tournament)}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{GAME_ICONS[tournament.game_type] || "🎮"}</span>
          <div>
            <div className="font-bold text-sm">{tournament.name}</div>
            <div className="text-xs text-gray-500">{GAME_NAMES[tournament.game_type]}</div>
          </div>
        </div>
        <span className={`${getStatusColor(tournament.status)} text-white text-[10px] px-2 py-0.5 rounded-full uppercase`}>
          {tournament.status.replace("_", " ")}
        </span>
      </div>
      
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-gray-600">
            <Users className="w-3 h-3" />
            {tournament.players?.length || 0}/{tournament.max_players}
          </span>
          <span className="flex items-center gap-1 text-yellow-600">
            <Coins className="w-3 h-3" />
            {tournament.entry_fee} entry
          </span>
          <span className="flex items-center gap-1 text-green-600">
            <Trophy className="w-3 h-3" />
            {tournament.prize_pool} prize
          </span>
        </div>
        
        {canJoin && (
          <button
            onClick={(e) => { e.stopPropagation(); onJoin(tournament); }}
            className="bg-green-500 text-white px-3 py-1 rounded text-xs font-bold hover:bg-green-600"
          >
            JOIN
          </button>
        )}
        {isJoined && tournament.status === "registration" && (
          <span className="bg-purple-500 text-white px-2 py-0.5 rounded text-[10px]">JOINED</span>
        )}
      </div>
      
      {tournament.winner && (
        <div className="mt-2 bg-yellow-100 rounded p-1 text-xs flex items-center gap-1 justify-center">
          <Crown className="w-3 h-3 text-yellow-600" />
          <span className="font-bold text-yellow-700">Winner: {tournament.winner}</span>
        </div>
      )}
    </div>
  );
};

// Create Tournament Modal
const CreateTournamentModal = ({ isOpen, onClose, onCreate }) => {
  const [name, setName] = useState("");
  const [gameType, setGameType] = useState("poker");
  const [maxPlayers, setMaxPlayers] = useState(8);
  const [entryFee, setEntryFee] = useState(100);
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);

  if (!isOpen) return null;

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Please enter a tournament name");
      return;
    }
    
    setCreating(true);
    try {
      await onCreate({ name, gameType, maxPlayers, entryFee, description });
      setName("");
      setDescription("");
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Create Tournament
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-bold text-gray-700 block mb-1">Tournament Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Friday Night Poker"
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              maxLength={50}
            />
          </div>

          <div>
            <label className="text-sm font-bold text-gray-700 block mb-1">Game</label>
            <select
              value={gameType}
              onChange={(e) => setGameType(e.target.value)}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            >
              {Object.entries(GAME_NAMES).map(([key, name]) => (
                <option key={key} value={key}>{GAME_ICONS[key]} {name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold text-gray-700 block mb-1">Max Players</label>
              <select
                value={maxPlayers}
                onChange={(e) => setMaxPlayers(Number(e.target.value))}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              >
                {[4, 8, 16, 32].map(n => (
                  <option key={n} value={n}>{n} players</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-bold text-gray-700 block mb-1">Entry Fee (F-Chips)</label>
              <select
                value={entryFee}
                onChange={(e) => setEntryFee(Number(e.target.value))}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              >
                {[0, 50, 100, 250, 500, 1000].map(n => (
                  <option key={n} value={n}>{n === 0 ? "Free" : `${n} F-Chips`}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-bold text-gray-700 block mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tournament rules or notes..."
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none h-20 resize-none"
              maxLength={200}
            />
          </div>

          <div className="bg-yellow-50 rounded-lg p-3 text-sm">
            <div className="flex items-center gap-2 text-yellow-700">
              <Coins className="w-4 h-4" />
              <span>Prize Pool: <strong>{entryFee * maxPlayers} F-Chips</strong> (winner takes all)</span>
            </div>
          </div>

          <button
            onClick={handleCreate}
            disabled={creating || !name.trim()}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-bold hover:from-purple-500 hover:to-indigo-500 disabled:opacity-50"
          >
            {creating ? "Creating..." : "CREATE TOURNAMENT"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Tournament Bracket View
const TournamentBracket = ({ tournament, onReportMatch, currentUser }) => {
  if (!tournament.bracket || tournament.bracket.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Swords className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>Bracket will appear when tournament starts</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-8 p-4 min-w-max">
        {tournament.bracket.map((round, roundIndex) => (
          <div key={roundIndex} className="flex flex-col gap-4">
            <div className="text-center font-bold text-sm text-purple-600 mb-2">
              {roundIndex === tournament.bracket.length - 1 ? "FINAL" : `Round ${round.round}`}
            </div>
            
            {round.matches.map((match, matchIndex) => (
              <div key={match.match_id} className="bg-white rounded-lg shadow border p-3 w-48">
                {/* Player 1 */}
                <div className={`flex items-center justify-between p-2 rounded ${
                  match.winner === match.player1 ? 'bg-green-100' : 
                  match.winner && match.winner !== match.player1 ? 'bg-red-50' : 'bg-gray-50'
                }`}>
                  <span className={`text-sm truncate ${match.winner === match.player1 ? 'font-bold text-green-700' : ''}`}>
                    {match.player1 || "TBD"}
                  </span>
                  <span className="text-sm font-bold">{match.score1}</span>
                </div>
                
                <div className="text-center text-gray-400 text-xs my-1">vs</div>
                
                {/* Player 2 */}
                <div className={`flex items-center justify-between p-2 rounded ${
                  match.winner === match.player2 ? 'bg-green-100' : 
                  match.winner && match.winner !== match.player2 ? 'bg-red-50' : 'bg-gray-50'
                }`}>
                  <span className={`text-sm truncate ${match.winner === match.player2 ? 'font-bold text-green-700' : ''}`}>
                    {match.player2 || "BYE"}
                  </span>
                  <span className="text-sm font-bold">{match.score2}</span>
                </div>
                
                {/* Report button for participants */}
                {match.status === "pending" && 
                 (match.player1 === currentUser || match.player2 === currentUser || tournament.host === currentUser) && (
                  <button
                    onClick={() => onReportMatch(match)}
                    className="w-full mt-2 bg-purple-500 text-white text-xs py-1 rounded hover:bg-purple-600"
                  >
                    Report Result
                  </button>
                )}
                
                {match.status === "completed" && (
                  <div className="text-center text-xs text-green-600 mt-1">✓ Complete</div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// Report Match Modal
const ReportMatchModal = ({ isOpen, onClose, match, onReport }) => {
  const [winner, setWinner] = useState("");
  const [score1, setScore1] = useState(1);
  const [score2, setScore2] = useState(0);
  const [reporting, setReporting] = useState(false);

  if (!isOpen || !match) return null;

  const handleReport = async () => {
    if (!winner) {
      toast.error("Please select a winner");
      return;
    }
    
    setReporting(true);
    try {
      await onReport(match.match_id, winner, score1, score2);
      onClose();
    } finally {
      setReporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
        <h3 className="text-lg font-bold mb-4">Report Match Result</h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-bold text-gray-700 block mb-2">Who won?</label>
            <div className="space-y-2">
              <button
                onClick={() => { setWinner(match.player1); setScore1(1); setScore2(0); }}
                className={`w-full p-3 rounded-lg border-2 text-left ${
                  winner === match.player1 ? 'border-green-500 bg-green-50' : 'border-gray-200'
                }`}
              >
                <span className="font-bold">{match.player1}</span>
              </button>
              {match.player2 && (
                <button
                  onClick={() => { setWinner(match.player2); setScore1(0); setScore2(1); }}
                  className={`w-full p-3 rounded-lg border-2 text-left ${
                    winner === match.player2 ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  }`}
                >
                  <span className="font-bold">{match.player2}</span>
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 py-2 bg-gray-200 rounded-lg font-medium hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleReport}
              disabled={!winner || reporting}
              className="flex-1 py-2 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600 disabled:opacity-50"
            >
              {reporting ? "Saving..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Tournament Panel
const TournamentPanel = () => {
  const { token, user } = useContext(AuthContext);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all, registration, in_progress, completed
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [reportMatch, setReportMatch] = useState(null);

  const fetchTournaments = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const response = await axios.get(`${API}/tournaments/list${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournaments(response.data.tournaments || []);
    } catch (error) {
      console.error("Failed to fetch tournaments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchTournaments();
      // Auto-refresh every 30 seconds
      const interval = setInterval(fetchTournaments, 30000);
      return () => clearInterval(interval);
    }
  }, [token, filter]);

  const handleCreateTournament = async (data) => {
    try {
      const response = await axios.post(`${API}/tournaments/create`, {
        name: data.name,
        game_type: data.gameType,
        max_players: data.maxPlayers,
        entry_fee: data.entryFee,
        description: data.description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Tournament created! Code: ${response.data.tournament_id}`);
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create tournament");
      throw error;
    }
  };

  const handleJoinTournament = async (tournament) => {
    try {
      await axios.post(`${API}/tournaments/${tournament.tournament_id}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Joined tournament!");
      fetchTournaments();
      if (selectedTournament?.tournament_id === tournament.tournament_id) {
        // Refresh selected tournament
        const response = await axios.get(`${API}/tournaments/${tournament.tournament_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedTournament(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join tournament");
    }
  };

  const handleStartTournament = async () => {
    if (!selectedTournament) return;
    try {
      await axios.post(`${API}/tournaments/${selectedTournament.tournament_id}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Tournament started!");
      const response = await axios.get(`${API}/tournaments/${selectedTournament.tournament_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTournament(response.data);
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start tournament");
    }
  };

  const handleReportMatch = async (matchId, winner, score1, score2) => {
    if (!selectedTournament) return;
    try {
      const response = await axios.post(`${API}/tournaments/${selectedTournament.tournament_id}/report_match`, {
        match_id: matchId,
        winner,
        score1,
        score2
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      
      // Refresh tournament
      const updated = await axios.get(`${API}/tournaments/${selectedTournament.tournament_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTournament(updated.data);
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to report match");
    }
  };

  const handleCancelTournament = async () => {
    if (!selectedTournament || !confirm("Cancel this tournament and refund all entry fees?")) return;
    try {
      await axios.post(`${API}/tournaments/${selectedTournament.tournament_id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Tournament cancelled, fees refunded");
      setSelectedTournament(null);
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel tournament");
    }
  };

  // Tournament Detail View
  if (selectedTournament) {
    const isHost = selectedTournament.host === user?.username;
    const canStart = isHost && selectedTournament.status === "registration" && selectedTournament.players?.length >= 2;
    
    return (
      <div className="retro-window h-full flex flex-col" data-testid="tournament-detail">
        <div className="retro-title-bar flex items-center justify-between px-2">
          <button onClick={() => setSelectedTournament(null)} className="text-xs hover:underline">← Back</button>
          <span className="text-xs font-bold">{selectedTournament.name}</span>
          <span className="text-[10px] bg-white/20 px-1 rounded">{selectedTournament.tournament_id}</span>
        </div>

        <div className="flex-1 p-3 bg-[#C0C0C0] overflow-y-auto">
          {/* Tournament Info */}
          <div className="bg-white rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{GAME_ICONS[selectedTournament.game_type]}</span>
                <div>
                  <div className="font-bold">{selectedTournament.name}</div>
                  <div className="text-xs text-gray-500">by {selectedTournament.host}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-yellow-600">
                  <Trophy className="w-4 h-4" />
                  <span className="font-bold">{selectedTournament.prize_pool} F-Chips</span>
                </div>
                <div className="text-xs text-gray-500">Prize Pool</div>
              </div>
            </div>
            
            {selectedTournament.description && (
              <p className="text-sm text-gray-600 mb-2">{selectedTournament.description}</p>
            )}
            
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {selectedTournament.players?.length}/{selectedTournament.max_players} players
              </span>
              <span className="flex items-center gap-1">
                <Coins className="w-3 h-3" />
                {selectedTournament.entry_fee} entry fee
              </span>
              <span className={`px-2 py-0.5 rounded-full text-white text-[10px] ${
                selectedTournament.status === "registration" ? "bg-green-500" :
                selectedTournament.status === "in_progress" ? "bg-yellow-500" :
                selectedTournament.status === "completed" ? "bg-blue-500" : "bg-red-500"
              }`}>
                {selectedTournament.status.replace("_", " ").toUpperCase()}
              </span>
            </div>

            {/* Host Controls */}
            {isHost && selectedTournament.status === "registration" && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={handleStartTournament}
                  disabled={!canStart}
                  className="flex-1 py-2 bg-green-500 text-white rounded font-bold text-sm hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  <Play className="w-4 h-4" /> Start Tournament
                </button>
                <button
                  onClick={handleCancelTournament}
                  className="py-2 px-4 bg-red-500 text-white rounded font-bold text-sm hover:bg-red-600"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Winner Banner */}
            {selectedTournament.winner && (
              <div className="mt-3 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-lg p-3 text-center">
                <Crown className="w-8 h-8 text-white mx-auto mb-1" />
                <div className="text-white font-bold text-lg">{selectedTournament.winner} WINS!</div>
                <div className="text-white/80 text-sm">🏆 {selectedTournament.prize_pool} F-Chips</div>
              </div>
            )}
          </div>

          {/* Players List */}
          <div className="bg-white rounded-lg p-3 mb-3">
            <h4 className="font-bold text-sm mb-2 flex items-center gap-1">
              <Users className="w-4 h-4" /> Players
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {selectedTournament.players?.map((player, i) => (
                <div key={i} className={`flex items-center justify-between p-2 rounded text-sm ${
                  player.eliminated ? 'bg-red-50 text-gray-400' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center gap-1">
                    {player.username === selectedTournament.host && <Crown className="w-3 h-3 text-yellow-500" />}
                    {player.username === selectedTournament.winner && <Medal className="w-3 h-3 text-yellow-500" />}
                    <span className={player.eliminated ? 'line-through' : ''}>{player.username}</span>
                  </div>
                  <span className="text-xs text-gray-500">{player.wins}W-{player.losses}L</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bracket */}
          <div className="bg-white rounded-lg p-3">
            <h4 className="font-bold text-sm mb-2 flex items-center gap-1">
              <Swords className="w-4 h-4" /> Bracket
            </h4>
            <TournamentBracket 
              tournament={selectedTournament} 
              onReportMatch={(match) => setReportMatch(match)}
              currentUser={user?.username}
            />
          </div>
        </div>

        {/* Report Match Modal */}
        <ReportMatchModal
          isOpen={!!reportMatch}
          match={reportMatch}
          onClose={() => setReportMatch(null)}
          onReport={handleReportMatch}
        />
      </div>
    );
  }

  // Tournament List View
  return (
    <div className="retro-window max-h-64 flex flex-col" data-testid="tournament-panel">
      <div className="retro-title-bar flex items-center justify-between px-2">
        <span className="flex items-center gap-1">
          <Trophy className="w-4 h-4" />
          <span className="text-xs font-bold">TOURNAMENTS</span>
        </span>
        <button onClick={fetchTournaments} disabled={loading} className="text-[10px] hover:bg-white/20 px-1 rounded">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="p-2 bg-[#C0C0C0] flex-1 flex flex-col overflow-hidden">
        {/* Filter & Create */}
        <div className="flex items-center gap-2 mb-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="retro-button text-xs py-1 px-2 flex-1"
          >
            <option value="all">All Tournaments</option>
            <option value="registration">Open Registration</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
          <button
            onClick={() => setShowCreateModal(true)}
            className="retro-button bg-green-500 text-white px-2 py-1 text-xs font-bold flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Create
          </button>
        </div>

        {/* Tournament List */}
        <div className="flex-1 overflow-y-auto retro-scrollbar space-y-2">
          {loading && tournaments.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">Loading...</div>
          ) : tournaments.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500 text-sm">No tournaments yet</p>
              <p className="text-gray-400 text-xs">Create one to get started!</p>
            </div>
          ) : (
            tournaments.map(tournament => (
              <TournamentCard
                key={tournament.tournament_id}
                tournament={tournament}
                currentUser={user?.username}
                onJoin={handleJoinTournament}
                onView={setSelectedTournament}
              />
            ))
          )}
        </div>
      </div>

      {/* Create Tournament Modal */}
      <CreateTournamentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateTournament}
      />
    </div>
  );
};

export default TournamentPanel;
